'use client';

import React from 'react';
import { EnvelopeClosedIcon } from '@radix-ui/react-icons';
import { Button, buttonVariants } from '@/components/ui/button';
import Image from 'next/image';
import Link from 'next/link';
import { cn } from '@/utils/cn';
import { createClient } from '@/utils/supabase/client';
import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingIndicator } from '@/components/common/loading-indicator';
import dynamic from 'next/dynamic';

const SenjaEmbed = dynamic(() => import('@/components/SenjaEmbed/SenjaEmbed'), {
  ssr: false,
});

const registerSchema = z.object({
  email: z.string().email(),
});

export default function Register() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const supabase = createClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
  });

  const handleEmailLogin = async (email: string) => {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
        shouldCreateUser: true,
      },
    });

    if (error) {
      setShowError(true);
      return;
    }

    setShowSuccess(true);
  };

  return (
    <div className="grid h-full min-h-screen grid-cols-1 gap-16 px-10  py-10 lg:grid-cols-3 lg:px-40">
      <div className="flex h-full w-full flex-col items-start justify-center">
        <Link href="/" className={'mb-6'}>
          <Image
            src={'/beatwave-logo.png'}
            alt={'Beatwave logo'}
            width={2040}
            height={458}
            className={'h-auto w-40'}
          />
        </Link>
        <div className="mb-6 text-4xl font-extrabold uppercase italic text-wave-brown">
          Sign Up
        </div>

        <form className={'mt-4 w-full space-y-6'}>
          <div className="group">
            <label
              htmlFor="email"
              className="text-xs font-medium leading-none group-focus-within:text-wave-brown"
            >
              Signup With Email
            </label>
            <div className="relative">
              <EnvelopeClosedIcon className="absolute left-5 h-full group-focus-within:text-wave-brown" />
              <input
                {...register('email')}
                type="email"
                placeholder="email@example.com"
                autoComplete="email"
                className={
                  'mt-1 block w-full rounded-full border-none bg-wave-dark pb-4 pl-12 pt-3.5 text-base leading-none text-highlights-white outline-none ring-2 ring-orange-0 ring-opacity-75 transition-all duration-300 ease-in-out placeholder:text-gray-600 focus:outline-none focus-visible:ring-wave-brown focus-visible:ring-opacity-75'
                }
              />
            </div>

            {errors.email?.message && (
              <p className="mt-2 text-xs text-wave-red">
                {errors.email.message}
              </p>
            )}
          </div>

          <Button
            variant={'secondary'}
            size={'lg'}
            className={'h-10 w-full italic'}
            disabled={isSubmitting}
            onClick={handleSubmit(async ({ email }) => handleEmailLogin(email))}
          >
            {isSubmitting ? <LoadingIndicator /> : <>Create Account</>}
          </Button>

          {showSuccess && (
            <p className={'text-sm text-green-500'}>
              We&apos;ve sent a login link to your email. Click it to log in.
            </p>
          )}

          {showError && (
            <p className={'text-sm text-wave-red'}>
              We encountered an error while creating your account. Please try
              again later.
            </p>
          )}

          <div className="h-px w-full bg-zinc-600" />

          <div className="space-y-2">
            <div className="leading font-bold uppercase italic text-highlights-white">
              Already a member?
            </div>
            <Link
              className={cn(
                buttonVariants({ variant: 'muted', size: 'default' }),
                'rounded-full'
              )}
              href={'/login'}
            >
              Login
            </Link>
          </div>

          <div className="mt-4 space-y-2">
            <div className="leading font-bold uppercase italic text-wave-brown">
              Want lifetime access?
            </div>
            <Link
              className={cn(
                buttonVariants({ variant: 'secondary', size: 'default' }),
                'rounded-full'
              )}
              href={'/lifetime'}
            >
              Get Lifetime Plan
            </Link>
          </div>
        </form>
      </div>

      <div className={'flex h-full items-center justify-center lg:col-span-2'}>
        <SenjaEmbed />
      </div>
    </div>
  );
}